import { Box, Button, Chip, Typography, useTheme } from "@mui/material";
import { AlertCircle } from "lucide-react";
import SparklesGradientIcon from "@/assets/icons/SparklesGradientIcon";
import { ActivityItem } from "@/modules/lead-page/dtos/index-v2";

interface WhatsappConversationInfoProps {
  item: ActivityItem;
  onViewConversation: () => void;
}

const CARD_RADIUS = "12px";

const formatLabel = (val: string) =>
  val.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const WhatsappConversationInfo = ({
  item,
  onViewConversation,
}: WhatsappConversationInfoProps) => {
  const theme = useTheme();

  const summary = item.whatsappSummary ?? null;
  const flags = item.whatsappFlags ?? null;
  const sentiment = item.whatsappSentiment ?? null;

  const callOutcome = flags?.outcome as string | undefined;
  const redFlags = flags?.red_flags as string[] | undefined;
  const tags = [sentiment?.overall_agent, sentiment?.overall_client].filter(
    (t): t is string => !!t,
  );

  const hasSummary = !!summary && summary.trim().length > 0;
  const hasCallOutcome = !!callOutcome;
  const hasRedFlags = !!redFlags && redFlags.length > 0;
  const hasTags = tags.length > 0;
  const hasSummaryCardContent = hasSummary || hasTags;
  return (
    <>
      {/* Summary card */}
      {hasSummaryCardContent && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "4px",
            px: theme.nawy.spacing.sm,
            py: theme.nawy.spacing.xs,
            borderRadius: CARD_RADIUS,
            backgroundColor: theme.nawy.colors.primitives.white[200],
          }}
        >
          <Box
            sx={{
              display: "flex",
              gap: "4px",
              height: 20,
              alignItems: "center",
            }}
          >
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 400,
                lineHeight: "16px",
                color: theme.nawy.colors.semantic.text.tertiary,
                whiteSpace: "nowrap",
              }}
            >
              Call summary
            </Typography>
            <SparklesGradientIcon size={16} />
          </Box>

          {hasTags && (
            <Box sx={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
              {tags.map((tag, i) => (
                <Chip
                  key={i}
                  label={tag}
                  size="small"
                  sx={{
                    fontSize: 12,
                    fontWeight: 400,
                    lineHeight: "18px",
                    color: theme.nawy.colors.semantic.text.primary,
                    backgroundColor: theme.nawy.colors.semantic.layer.primary,
                    borderRadius: "4px",
                    height: "auto",
                    "& .MuiChip-label": {
                      px: theme.nawy.spacing.xs,
                      py: "2px",
                    },
                  }}
                />
              ))}
            </Box>
          )}

          {hasSummary && (
            <Typography
              sx={{
                ...theme.nawy.typography.english.variants.body.md.regular,
                color: theme.nawy.colors.semantic.text.primary,
              }}
            >
              {summary}
            </Typography>
          )}
        </Box>
      )}

      {/* Conversation outcome card */}
      {hasCallOutcome && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            p: "12px",
            borderRadius: CARD_RADIUS,
            backgroundColor: theme.nawy.colors.primitives.white[200],
          }}
        >
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 400,
              lineHeight: "16px",
              color: theme.nawy.colors.semantic.text.secondary,
              whiteSpace: "nowrap",
            }}
          >
            Conversation outcome
          </Typography>
          <Chip
            label={formatLabel(callOutcome!)}
            size="small"
            sx={{
              fontSize: 11,
              fontWeight: 400,
              lineHeight: "16px",
              backgroundColor: theme.nawy.colors.semantic.indicator.blue,
              color: theme.nawy.colors.semantic.indicator.onColor,
              borderRadius: "4px",
              height: "auto",
              "& .MuiChip-label": { px: theme.nawy.spacing.xs, py: "2px" },
            }}
          />
        </Box>
      )}

      {/* Red flags card */}
      {hasRedFlags && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "4px",
            p: "12px",
            borderRadius: CARD_RADIUS,
            backgroundColor:
              theme.nawy.colors.semantic.layer.extension.warningLight,
          }}
        >
          {redFlags!.map((flag, idx) => (
            <Box
              key={idx}
              sx={{ display: "flex", alignItems: "center", gap: "4px" }}
            >
              <AlertCircle
                size={16}
                color={theme.nawy.colors.semantic.indicator.orange}
                style={{ flexShrink: 0 }}
              />
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 400,
                  lineHeight: "18px",
                  color: theme.nawy.colors.semantic.text.primary,
                }}
              >
                {flag}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {/* View detailed conversation link */}
      <Button
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          onViewConversation();
        }}
        sx={{
          alignSelf: "flex-start",
          textTransform: "none",
          ...theme.nawy.typography.english.variants.body.sm.semibold,
          color: theme.nawy.colors.semantic.text.accent,
          px: 0,
          minWidth: "auto",
          "&:hover": {
            background: "transparent",
            textDecoration: "underline",
          },
        }}
      >
        {"View detailed conversation →"}
      </Button>
    </>
  );
};

export default WhatsappConversationInfo;
